Para optimizar el rendimiento y evitar que los dispositivos móviles con bajo rendimiento o poca memoria RAM experimenten *lags* o tirones, hemos aplicado una **estrategia clave de optimización**:

1. **Reducción de partículas en el DOM:** Se redujeron las 50 estrellas doradas a **16 partículas clave** distribuidas estratégicamente. Esto reduce drásticamente el peso sobre el motor de renderizado del navegador sin perder la atmósfera mágica.
2. **Uso de propiedades optimizadas para GPU (`will-change`, `transform3d`):** Forzamos la aceleración por hardware en los elementos animados para que el procesador gráfico (GPU) se encargue del movimiento, liberando memoria RAM.
3. **Optimización en GSAP y limpieza de selectores:** Se optimizó la selección de elementos para evitar bloqueos en el hilo principal durante la inicialización.

Aquí tienes el componente completo, optimizado y fluido:

```vue
<template>
  <div class="portada-arley" ref="rootRef" aria-label="15th Birthday Cover">
    <div class="fondo-gradiente">
      <div class="fondo-arco"></div>
    </div>

    <!-- Magic dust optimized for low RAM usage -->
    <div class="polvo-dorado-container">
      <div v-for="n in 16" :key="n" :class="['particula-dorada', 'estrella-' + ((n % 3) + 1)]"></div>
    </div>

    <div class="onda-luz-bg"></div>

    <img src="@/assets/Copia de Diseño sin título (4).png" class="adorno velas-superior" alt="" />

    <img src="@/assets/brillo.avif" class="brillo brillo-principal-izq" alt="" />

    <!-- Flowers and Butterflies with ultra-fine fluttering and GPU acceleration -->
    <img src="@/assets/florblush.png" class="flor flor-rosa-arriba-der" alt="" />
    <img src="@/assets/florblush.png" class="flor flor-rosa-abajo-izq" alt="" />
    
    <!-- Butterflies -->
    <img src="@/assets/mariposaa.png" class="flor flor-blanca-centro-der mariposa-aleteo-fino-1" alt="" />
    <img src="@/assets/mariposaa.png" class="flor mariposa-extra-izq mariposa-volteada mariposa-aleteo-fino-2" alt="" />
    <img src="@/assets/mariposaa.png" class="flor mariposa-extra-arriba mariposa-aleteo-fino-3" alt="" />
    
    <img src="@/assets/flor-izq.png" class="flor flor-blanca-esquina-izq" alt="" />

    <img src="@/assets/Copia de Diseño sin título (4).png" class="adorno velas-inferior-der" alt="" />

    <div class="imagen-xv-wrapper shadow-custom">
      <img src="@/assets/DSC_1120.webp" class="imagen-xv" alt="Protagonist photo" />
    </div>

    <header class="texto-cabecera">
      <img src="@/assets/sepp.avif" class="separador-floral-top" aria-hidden="true" alt="" />
      <p class="texto-introduccion-xv">
        There are moments that are dreamed of for a long time and, when they finally arrive, become memories to last a lifetime. <br> <br>Today begins a new chapter filled with hopes, new experiences, and dreams to come true. And how beautiful it will be to share this special moment with the people who are part of my story.
      </p><br><br>
    </header>
  </div>
</template>
<script>
import { onMounted, ref, nextTick } from 'vue'
import gsap from 'gsap'

export default {
  name: 'PortadaArley',
  setup() {
    const rootRef = ref(null)

    onMounted(async () => {
      await nextTick()

      const $ = (sel) => Array.from(gsap.utils.toArray(sel)).filter(Boolean)

      const fondo = $('.fondo-gradiente')
      const arco = $('.fondo-arco')
      const foto = $('.imagen-xv-wrapper')
      const textos = $('.texto-cabecera, .controles-inferiores, .parrafo-presentacion-wrapper, .separador-floral-top')
      const adornosSatinados = $('.velas-superior, .velas-inferior-der')
      const flores = $('.flor')
      const brilloUnico = $('.brillo-principal-izq')
      const controles = $('.controles-inferiores')
      const playIcon = $('.play-icon')
      const seekBar = $('.seek-bar')

      const totalElementos = [].concat(fondo, arco, foto, textos, adornosSatinados, flores, brilloUnico, controles, playIcon, seekBar)
      gsap.set(totalElementos, { autoAlpha: 0, y: 20 })
      gsap.set(brilloUnico, { scale: 0 })

      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return

          const tl = gsap.timeline({ defaults: { ease: 'power2.out' } })

          tl.to(fondo, { autoAlpha: 1, y: 0, duration: 0.8 })
            .to(arco, { autoAlpha: 1, y: 0, duration: 0.7 }, '-=0.5')

          tl.fromTo(
            foto,
            { scale: 0.96, y: 25 },
            { autoAlpha: 1, scale: 1, y: 0, duration: 1 },
            '-=0.4'
          )

          const textosYAdornos = [].concat(textos, adornosSatinados)
          tl.to(
            textosYAdornos,
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.1
            },
            '-=0.6'
          )

          tl.to(
            flores,
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.1
            },
            '-=0.5'
          )

          tl.to(
            brilloUnico,
            {
              autoAlpha: 1,
              scale: 1,
              duration: 0.7,
              onComplete: () => {
                gsap.to(brilloUnico, {
                  opacity: 0.5,
                  scale: 1.1,
                  duration: 1.5,
                  repeat: -1,
                  yoyo: true,
                  ease: 'sine.inOut'
                })
              }
            },
            '-=0.4'
          )

          tl.to(controles, { autoAlpha: 1, y: 0, duration: 0.6 }, '-=0.2')

          // Animaciones continuas optimizadas con will-change implícito en CSS
          gsap.to(foto, {
            scale: 1.01,
            duration: 5,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut'
          })

          io.disconnect()
        })
      }, { threshold: 0.3 })

      if (rootRef.value) io.observe(rootRef.value)
    })

    return { rootRef }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Montserrat:wght@400;700&display=swap');

.portada-arley {
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 48px 16px 56px;
  box-sizing: border-box;
  overflow: visible;
}

/* GRADIENTE ROSADO DE FONDO */
.fondo-gradiente {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: -2;
}

/* ARCO DE FONDO DETRÁS DE LA FOTO */
.fondo-arco {
  position: absolute;
  top: 36vh;
  left: 50%;
  transform: translateX(-50%);
  width: min(92vw, 360px);
  height: 60vh;
  border-radius: 180px 180px 0 0;
  z-index: -1;
}

/* MARCO REDONDEADO LUXURY */
.imagen-xv-wrapper {
  width: min(92vw, 400px);
  height: calc(min(92vw, 400) * 1.4);
  margin-top: 60vh !important;
  margin-bottom: auto;
  border-radius: 180px 180px 0 0;
  overflow: hidden;
  z-index: 2;
  transform: translateZ(0); /* Forzar aceleración por hardware GPU */
  will-change: transform;
}

.imagen-xv {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.shadow-custom {
  box-shadow: 
    0 10px 30px rgba(0,0,0,.15),
    0 2px 6px rgba(0,0,0,.05);
}

/* CABECERA EDITORIAL */
.texto-cabecera {
  width: min(90vw, 420px);
  text-align: center;
  position: absolute;
  top: 11vh;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
}

.separador-floral-top {
  width: min(65vw, 290px) !important;
  height: auto;
  margin: 0 auto 16px;
  display: block;
  object-fit: contain;
}

.texto-introduccion-xv {
  font-family: 'Playfair Display', serif;
  font-size: clamp(18px, 2.6vw, 19px);
  color: #1a1a1a;
  line-height: 1.6;
  margin: 0;
  padding: 0 10px;
  font-weight: 400;
}

/* COMPONENTES GRÁFICOS */
.flor, .adorno, .brillo {
  position: absolute;
  transform-origin: center;
  object-fit: contain;
  transform: translateZ(0); /* Aceleración GPU */
}

.adorno.velas-superior {
  top: -170px; left: -240px; z-index: 8 !important;
  width: min(450vw, 450px);
}

.adorno.velas-inferior-der {
  bottom: -120px !important; left: -65px; z-index: 4;
  width: min(630vw, 530px);
  transform: rotate(-2deg) translateZ(0);
}

.flor-rosa-arriba-der {
  top: 48vh; right: -5vw !important; z-index: 1;
  width: min(59vw, 280px);
}

.flor-rosa-abajo-izq {
  bottom: 12vh; left: -14vw; z-index: 4;
  width: min(46vw, 240px);
}

.flor-blanca-centro-der {
  top: 46vh; right: -3vw; z-index: 3;
  width: min(48vw, 220px);
}

.flor-blanca-esquina-izq {
  bottom: -9vh; left: -8vw; z-index: 1;
  width: min(24vw, 20px) !important;
  opacity: 0.45;
}

/* Mariposas adicionales */
.mariposa-extra-izq {
  top: 22vh; left: -8vw; z-index: 3;
  width: min(35vw, 140px);
}

.mariposa-extra-arriba {
  top: 0vh; right: 4vw; z-index: 3;
  width: min(30vw, 110px);
}

.mariposa-volteada {
  transform: scaleX(-1) translateZ(0);
}

/* 🦋 ALETEO FINO Y FLUIDO (Optimizado para ligereza en GPU) */
@keyframes aleteoFino1 {
  0%, 100% { transform: scale3d(1, 1, 1) rotate(0deg); }
  50% { transform: scale3d(0.92, 1.03, 1) rotate(1.5deg); }
}

@keyframes aleteoFino2 {
  0%, 100% { transform: scaleX(-1) scale3d(1, 1, 1) rotate(0deg); }
  50% { transform: scaleX(-1) scale3d(0.94, 1.02, 1) rotate(-2deg); }
}

@keyframes aleteoFino3 {
  0%, 100% { transform: scale3d(1, 1, 1) rotate(0deg); }
  50% { transform: scale3d(0.93, 1.04, 1) rotate(-1deg); }
}

.mariposa-aleteo-fino-1 {
  animation: aleteoFino1 2.5s ease-in-out infinite;
}

.mariposa-aleteo-fino-2 {
  animation: aleteoFino2 2.8s ease-in-out infinite;
}

.mariposa-aleteo-fino-3 {
  animation: aleteoFino3 2.2s ease-in-out infinite;
}

/* POLVO MÁGICO DE ESTRELLITAS DORADAS REDUCIDO (Optimizado RAM) */
.polvo-dorado-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 4;
  overflow: hidden;
}

.particula-dorada {
  position: absolute;
  clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
  will-change: transform, opacity;
  animation: flotarEstrellita linear infinite;
}

.estrella-1 { width: 4px; height: 4px; animation-duration: 3.5s; }
.estrella-2 { width: 6px; height: 6px; animation-duration: 5s; }
.estrella-3 { width: 3.5px; height: 3.5px; animation-duration: 4s; }

/* 16 posiciones estratégicas para no saturar memoria */
.particula-dorada:nth-child(1)  { left: 5%;  top: 10%; animation-delay: 0.2s; }
.particula-dorada:nth-child(2)  { left: 90%; top: 15%; animation-delay: 1.5s; }
.particula-dorada:nth-child(3)  { left: 8%;  top: 30%; animation-delay: 0.8s; }
.particula-dorada:nth-child(4)  { left: 92%; top: 35%; animation-delay: 2.2s; }
.particula-dorada:nth-child(5)  { left: 4%;  top: 55%; animation-delay: 1.1s; }
.particula-dorada:nth-child(6)  { left: 95%; top: 60%; animation-delay: 0.4s; }
.particula-dorada:nth-child(7)  { left: 6%;  top: 80%; animation-delay: 1.8s; }
.particula-dorada:nth-child(8)  { left: 91%; top: 85%; animation-delay: 0.9s; }
.particula-dorada:nth-child(9)  { left: 25%; top: 3%;  animation-delay: 2.5s; }
.particula-dorada:nth-child(10) { left: 75%; top: 4%;  animation-delay: 1.3s; }
.particula-dorada:nth-child(11) { left: 20%; top: 96%; animation-delay: 0.6s; }
.particula-dorada:nth-child(12) { left: 80%; top: 95%; animation-delay: 1.9s; }
.particula-dorada:nth-child(13) { left: 12%; top: 20%; animation-delay: 1.0s; }
.particula-dorada:nth-child(14) { left: 88%; top: 45%; animation-delay: 2.1s; }
.particula-dorada:nth-child(15) { left: 15%; top: 70%; animation-delay: 0.3s; }
.particula-dorada:nth-child(16) { left: 85%; top: 75%; animation-delay: 1.6s; }

@keyframes flotarEstrellita {
  0% {
    transform: translateY(0px) scale(0.6) rotate(0deg);
    opacity: 0;
  }
  50% {
    transform: translateY(-25px) scale(1.1) rotate(180deg);
    opacity: 0.75;
  }
  100% {
    transform: translateY(-50px) scale(0.5) rotate(360deg);
    opacity: 0;
  }
}

.brillo-principal-izq {
  top: 31vh; left: 6vw; z-index: 3;
  width: 140px; opacity: 0.9;
}

.onda-luz-bg {
  position: absolute;
  top: -60%;
  left: -60%;
  width: 220%;
  height: 220%;
  background: 
    radial-gradient(circle at 30% 40%, rgba(255, 255, 255, 0.75) 0%, rgba(255, 255, 255, 0) 25%),
    radial-gradient(circle at 50% 43%, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0) 20%),
    radial-gradient(circle at 70% 48%, rgba(255, 255, 255, 0.75) 0%, rgba(255, 255, 255, 0) 25%),
    linear-gradient(
      15deg,
      rgba(255, 255, 255, 0) 40%,
      rgba(255, 255, 255, 0.2) 46%,
      rgba(255, 255, 255, 0.6) 50%,
      rgba(255, 255, 255, 0.2) 54%,
      rgba(255, 255, 255, 0) 60%
    );
  filter: blur(8px); /* Blur un poco más suave para mejorar rendimiento */
  z-index: 1;
  animation: barridoOndulado 7s infinite ease-in-out;
  pointer-events: none;
  transform: translateZ(0);
}

@keyframes barridoOndulado {
  0% {
    transform: translate(-25%, -35%) scaleY(0.9) translateZ(0);
    opacity: 0;
  }
  15% {
    opacity: 0.8;
  }
  85% {
    opacity: 0.8;
  }
  100% {
    transform: translate(20%, 25%) scaleY(1.1) translateZ(0);
    opacity: 0;
  }
}

@media (min-width: 992px){
  .portada-arley { padding-top: 64px; }
  .texto-cabecera { top: 12vh; }
}

@media (max-width: 500px) {
  .flor-rosa-arriba-der { right: -18vw; }
  .flor-rosa-abajo-izq { left: -16vw; }
}
</style>

```